import React, { useCallback } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { EXPRESSION_LABELS } from "../../../constants/expressionBuilder.constants";
import "../../../styles/ExpressionBuilder.css";

/**
 * Component for rendering logical operator buttons (AND/OR)
 * @param {Object} group - Group configuration
 * @param {Function} onUpdate - Group update handler
 * @returns {JSX.Element} Operator buttons component
 * @author Abhishek Dhillon
 * @parent GroupComponent
 */
const OperatorButtons = ({ group, onUpdate, isRoot = false }) => {
  /**
   * Updates group operation type
   * @param {string} operation - Operation type (AND/OR)
   * @author Abhishek Dhillon
   */
  const handleOperationChange = useCallback(
    (operation) => {
      onUpdate({ ...group, operation });
    },
    [group, onUpdate]
  );

  /**
   * Handles AND button click
   * @author Abhishek Dhillon
   */
  const handleAndClick = useCallback(() => {
    handleOperationChange("AND");
  }, [handleOperationChange]);

  /**
   * Handles OR button click
   * @author Abhishek Dhillon
   */
  const handleOrClick = useCallback(() => {
    handleOperationChange("OR");
  }, [handleOperationChange]);

  return (
    <div className='expression-builder-operator-container'>
      {isRoot ? (
        <div className={`btn-group expression-builder-operator-group expression-operator-btn-root`} role='group'>
          <Button
            onClick={handleAndClick}
            className={`btn-sm ${group?.operation === "AND" ? "operator-btn-selected" : "operator-btn-unselected"}`}
            id={`and-operator-${group?.id}`}
            rounded={"full"}>
            {EXPRESSION_LABELS.AND}
          </Button>
          <Button
            onClick={handleOrClick}
            className={`btn-sm ${group?.operation === "OR" ? "operator-btn-selected" : "operator-btn-unselected"}`}
            id={`or-operator-${group?.id}`}
            rounded={"full"}>
            {EXPRESSION_LABELS.OR}
          </Button>
        </div>
      ) : (
        <div className={`btn-group expression-builder-operator-group expression-operator-btn-not-root`} role='group'>
          <Button
            onClick={handleAndClick}
            themeColor={group?.operation === "AND" ? "primary" : "secondary"}
            fillMode={group?.operation === "AND" ? "solid" : "outline"}
            className={`btn-sm`}
            id={`and-operator-${group?.id}`}
            rounded={"full"}>
            {EXPRESSION_LABELS.AND}
          </Button>
          <Button
            onClick={handleOrClick}
            themeColor={group?.operation === "OR" ? "primary" : "secondary"}
            fillMode={group?.operation === "OR" ? "solid" : "outline"}
            className={`btn-sm ml-2`}
            id={`or-operator-${group?.id}`}
            rounded={"full"}>
            {EXPRESSION_LABELS.OR}
          </Button>
        </div>
      )}
    </div>
  );
};

export default OperatorButtons;
